import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDateAdapter, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { STUDIO_INQUIRY_COSTUMES_RENTALS } from '../../constants/common';
import { INDEX } from '../../enum/shared.enum';
import { CustomAdapter } from '../../services/date-picker/new-date-picker-service.service';
import { CustomDateParserFormatter } from '../../services/date-picker/new-date-picker.service';
import { FormValidationService } from '../../services/form-validations/form-validation.service';

interface CountryOption {
  countryId: string;
  countryName: string;
  states?: { stateId: string; stateName: string }[];
}

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'app-studio-inquiry-costumes-rentals',
    templateUrl: './studio-inquiry-costumes-rentals.component.html',
    styleUrls: ['./studio-inquiry-costumes-rentals.component.scss'],
    standalone: false,
    providers: [
        { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
        { provide: NgbDateAdapter, useClass: CustomAdapter },
    ]
})
export class StudioInquiryCostumesRentalsComponent implements OnInit {
  /** Everything captured on the earlier steps of the inquiry */
  @Input() projectInformation: any;
  /** The production company step, whose address seeds the billing address here */
  @Input() productionCompany: any;
  /**
   * Country list in the same shape as the company metadata endpoint
   * (`countryDetails`), each country carrying its own nested states.
   */
  @Input() countries: CountryOption[] = [];
  /** Values captured earlier, so stepping back and forward does not lose them */
  @Input() costumesRentals: any;
  @Output() costumesRentalsSubmitted = new EventEmitter<any>();
  constants = STUDIO_INQUIRY_COSTUMES_RENTALS;
  rentalsForm!: FormGroup;
  states: { stateId: string; stateName: string }[] = [];
  preferredDateFormat: number;
  formatId: INDEX;
  calenderFormat: INDEX;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly formValidationService: FormValidationService,
    private readonly dateAdapter: NgbDateAdapter<string>,
    private readonly dateFormatter: NgbDateParserFormatter,
    public activeModal: NgbActiveModal
  ) { }

  ngOnInit(): void {
    this.changeDateFormat();
    this.rentalsForm = this.formBuilder.group({
      description: [''],
      pullDate: ['', [Validators.required, this.formValidationService.dateValidatorNew(this.preferredDateFormat)]],
      racks: ['', Validators.required],
      visitCollection: ['', Validators.required],
      primaryContactType: ['', Validators.required],
      firstName: ['', [Validators.required, Validators.maxLength(50)]],
      lastName: ['', [Validators.required, Validators.maxLength(50)]],
      phone: ['', [Validators.required, this.formValidationService.validateTrPhoneNo]],
      cloudCheckIn: ['', Validators.required],
      orderShipped: ['', Validators.required],
      country: [''],
      street: ['', Validators.maxLength(85)],
      city: ['', Validators.maxLength(50)],
      state: [''],
      zipCode: [''],
      addressPhone: [''],
      billingDifferent: [false],
      billingContactName: ['', Validators.maxLength(50)],
      billingEmail: ['', this.formValidationService.validateEmail],
      paymentType: ['', Validators.required],
      useBillingForAccountant: [false],
      accountantName: [''],
      accountantEmail: [''],
    });
    this.prefillAddress();
    if (this.costumesRentals) {
      this.rentalsForm.patchValue(this.costumesRentals);
    }
    this.setStates(this.rentalsForm.get('country')?.value);
    this.watchPaymentType();
    this.getCalendarFormat();
    this.calenderFormat = this.formatId === INDEX.one ? INDEX.seven : INDEX.one;
  }

  /**
   * isLineOfCredit() - Reports whether the line of credit follow up is showing
   * @returns true when the applicant asked for a line of credit
   */
  get isLineOfCredit(): boolean {
    return this.rentalsForm?.get('paymentType')?.value === this.constants.lineOfCredit;
  }

  /**
   * changeDateFormat() - Reads the user's preferred date format, which drives
   * both the placeholder and how the picker parses what is typed. The inquiry is
   * open to visitors who have no saved preference, so this falls back to
   * m/d/yyyy and shares that with the adapter and formatter, which otherwise
   * discard every date they are handed
   */
  changeDateFormat(): void {
    const userProfile = JSON.parse(localStorage.getItem('user-profile'));
    const updatedData = JSON.parse(localStorage.getItem('updated-profile'));
    const dateFormatId = updatedData
      ? updatedData?.userPreferences?.dateFormatSettings?.dateFormatId
      : userProfile?.userPreferences?.dateFormatSettings?.dateFormatId;
    this.preferredDateFormat = dateFormatId || INDEX.one;
    (this.dateAdapter as CustomAdapter).preferredDateFormat = this.preferredDateFormat;
    (this.dateFormatter as CustomDateParserFormatter).preferredDateFormat = this.preferredDateFormat;
  }

  /**
   * getCalendarFormat() - Reads the user's preferred first day of the week,
   * defaulting to a Sunday start for visitors with no saved preference
   */
  getCalendarFormat(): void {
    const updatedData = JSON.parse(localStorage.getItem('updated-profile'));
    const userProfile = JSON.parse(localStorage.getItem('user-profile'));
    const calenderFormatId = updatedData
      ? updatedData?.userPreferences?.calenderFormatSettings?.calenderFormatId
      : userProfile?.userPreferences?.calenderFormatSettings?.calenderFormatId;
    this.formatId = calenderFormatId || INDEX.one;
  }

  /**
   * getPlaceholder() - Renders the placeholder for the preferred date format
   * @returns the date format hint
   */
  getPlaceholder(): string {
    if (this.preferredDateFormat === INDEX.one) {
      return 'mm/dd/yyyy';
    } else if (this.preferredDateFormat === INDEX.two) {
      return 'dd/mm/yyyy';
    }
    return 'yyyy-mm-dd';
  }

  /**
   * onCountryChange() - Swaps the state list to the ones belonging to the country
   * @param event the country select change event
   */
  onCountryChange(event: Event): void {
    const countryId = (event?.target as HTMLSelectElement)?.value;
    this.setStates(countryId);
    this.rentalsForm.get('state')?.setValue('');
  }

  /**
   * setStates() - Resolves the nested state list for the given country
   * @param countryId the selected country's id
   */
  setStates(countryId: string): void {
    this.states = this.countries?.find((country) => country.countryId === countryId)?.states || [];
  }

  /**
   * onNext() - Emits the costumes rentals details and closes the modal
   */
  onNext(): void {
    const costumesRentals = { ...this.rentalsForm.value };
    this.costumesRentalsSubmitted.emit(costumesRentals);
    this.activeModal.close(costumesRentals);
  }

  /**
   * onSkip() - Moves on to the review step without any costumes rentals details
   */
  onSkip(): void {
    this.costumesRentalsSubmitted.emit(null);
    this.activeModal.close(null);
  }

  /**
   * onBack() - Returns to the studio operations step
   */
  onBack(): void {
    this.activeModal.dismiss('back');
  }

  /**
   * closeModal() - Dismisses the inquiry
   */
  closeModal(): void {
    this.activeModal.dismiss();
  }

  /**
   * prefillAddress() - Seeds the billing address with the production company's,
   * which is the same address in every case the design shows
   */
  private prefillAddress(): void {
    if (!this.productionCompany) {
      return;
    }
    const { country, street, city, state, zipCode, addressPhone } = this.productionCompany;
    this.rentalsForm.patchValue({ country, street, city, state, zipCode, addressPhone });
  }

  /**
   * watchPaymentType() - Requires the accountant details only while the line of
   * credit follow up is on screen, so the other payment types stay submittable
   */
  private watchPaymentType(): void {
    const applyAccountantValidators = () => {
      ['accountantName', 'accountantEmail'].forEach((field) => {
        const control = this.rentalsForm.get(field);
        const validators = field === 'accountantEmail'
          ? [Validators.required, this.formValidationService.validateEmail]
          : [Validators.required, Validators.maxLength(50)];
        this.isLineOfCredit ? control?.setValidators(validators) : control?.clearValidators();
        control?.updateValueAndValidity({ emitEvent: false });
      });
    };
    applyAccountantValidators();
    this.rentalsForm.get('paymentType')?.valueChanges.subscribe(() => applyAccountantValidators());
  }
}
